import React from 'react';
import clsx from 'clsx';
import { Link, NavLink } from 'react-router-dom';
import { BookOpenIcon, ChevronRightIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import {
  BookOpenIcon as BookOpenIconSolid,
  UserGroupIcon as UserGroupIconSolid
} from '@heroicons/react/24/solid';
import { CaraMark, CaraWordMark } from '@oysterjs/ui/logo';

import { useCurrentAgencyUser } from './current-user';
import { SearchRailIcon, SearchRow } from './search';
import {
  NavigationAccess,
  NavigationGroup,
  NavigationPage,
  NavigationTab,
  filterNavigationGroups,
  filterNavigationTabs,
  navigationGroups,
  settingsPage,
  useIsHrefActive,
  useNavigationAccess
} from './navigation';

const COMMUNITY_URL = 'https://community.getcara.ai';

/** Row heights the disclosure's collapsed-to-open height is computed from. */
const TAB_ROW_HEIGHT = 28; // h-7
const TAB_ROW_GAP = 4; // h-1

const SIDEBAR_COLLAPSED_KEY = 'cara.sidebar.collapsed';

const readCollapsedPreference = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    // Safari in private browsing throws on any localStorage access.
    return false;
  }
};

/**
 * Whether the sidebar shows as the 64px rail. `forced` covers the routes that
 * collapse it regardless of preference (workspace and document detail); it
 * overrides the stored choice without overwriting it, so leaving such a route
 * restores whatever the user last picked.
 */
export const useSidebarCollapsed = (forced: boolean) => {
  const [preferred, setPreferred] = React.useState(readCollapsedPreference);

  const setCollapsed = React.useCallback((next: boolean) => {
    setPreferred(next);
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
    } catch {
      // Preference is a nicety; losing it is not worth failing the click over.
    }
  }, []);

  return { collapsed: forced || preferred, setCollapsed, canToggle: !forced };
};

/** The wireframe's panel glyph. Heroicons has no equivalent outline icon. */
const PanelIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
    <path d="M9.5 4.5v15" />
  </svg>
);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const pageRowClassName = (active: boolean) =>
  clsx(
    // text-left because the Documentation row is a button, which would otherwise
    // centre its label while the neighbouring link rows start theirs.
    'flex h-9 items-center gap-2.5 rounded-md px-2.5 text-left text-sm tracking-[-0.06px]',
    'transition-colors duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
    active
      ? 'bg-primary-50 dark:bg-primary-500/10 font-semibold text-primary-600 dark:text-primary-400 border-primary-100 dark:border-primary-500/20'
      : 'font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white border-transparent'
  );

const PageRowBody = ({
  page,
  active,
  disclosed
}: {
  page: Pick<NavigationPage, 'name' | 'icon' | 'activeIcon'>;
  active?: boolean;
  /** Omitted for rows with no sub-tabs, which draw no chevron at all. */
  disclosed?: boolean;
}) => {
  const Icon = active ? page.activeIcon : page.icon;

  return (
    <>
      <Icon aria-hidden="true" className="size-4 shrink-0 opacity-90" />
      <span className="flex-1 truncate">{page.name}</span>
      {disclosed !== undefined && (
        <ChevronRightIcon
          aria-hidden="true"
          strokeWidth={2}
          className={clsx(
            'size-3 shrink-0 opacity-50',
            'transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
            disclosed && 'rotate-90'
          )}
        />
      )}
    </>
  );
};

/**
 * The sub-tabs of the active page, unfolding beneath it. Kept mounted while
 * collapsed so both directions animate; `data-open` is what takes the rows out
 * of the tab order once the collapse finishes.
 */
const TabDisclosure = ({
  page,
  tabs,
  open,
  onNavigate
}: {
  page: NavigationPage;
  tabs: NavigationTab[];
  open: boolean;
  onNavigate?: () => void;
}) => {
  const isHrefActive = useIsHrefActive();

  return (
    <ul
      data-open={open}
      className="sidebar-disclosure"
      style={{
        maxHeight: open ? tabs.length * (TAB_ROW_HEIGHT + TAB_ROW_GAP) + 4 : 0,
        opacity: open ? 1 : 0
      }}
    >
      {tabs.map((tab, index) => {
        const active = isHrefActive(`${page.href}/${tab.href}`);

        return (
          <li key={tab.href}>
            <NavLink
              to={`${page.href}/${tab.href}`}
              onClick={onNavigate}
              tabIndex={open ? undefined : -1}
              style={{
                // Rows fan out on open and fold back in reverse, so the list
                // reads as one object rather than five independent rows.
                transitionDelay: `0ms, 0ms, ${open ? index * 30 : (tabs.length - 1 - index) * 18}ms`
              }}
              className={clsx(
                'sidebar-disclosure-row mb-px ml-6 my-1 flex rounded-md py-0 px-3 text-xs',
                open ? 'translate-x-0' : '-translate-x-2',
                active
                  ? 'bg-neutral-100 dark:bg-neutral-700 font-semibold text-neutral-900 dark:text-white'
                  : 'font-medium text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5'
              )}
            >
              <span className="truncate leading-7">{tab.name}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
};

const PageRow = ({
  page,
  tabs,
  onNavigate
}: {
  page: NavigationPage;
  tabs: NavigationTab[];
  onNavigate?: () => void;
}) => {
  const isHrefActive = useIsHrefActive();
  const active = isHrefActive(page.href);
  // Tabs follow the route rather than a separate open/closed state: the tabs
  // you can reach are always the ones belonging to where you already are.
  const disclosed = active && tabs.length > 0;

  return (
    <li className="mb-0.5">
      <NavLink
        to={page.href}
        onClick={onNavigate}
        className={clsx(pageRowClassName(active), 'w-full')}
      >
        <PageRowBody
          page={page}
          active={active}
          disclosed={tabs.length > 0 ? disclosed : undefined}
        />
      </NavLink>
      {tabs.length > 0 && (
        <TabDisclosure page={page} tabs={tabs} open={disclosed} onNavigate={onNavigate} />
      )}
    </li>
  );
};

const UserSummary = ({ compact }: { compact?: boolean }) => {
  const agencyUser = useCurrentAgencyUser();
  const avatar = (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-500/20 text-xs font-semibold text-primary-600 dark:text-primary-400',
        compact ? 'size-8' : 'size-7'
      )}
    >
      {initials(agencyUser.name)}
    </div>
  );

  if (compact) {
    return <div title={`${agencyUser.name} — ${agencyUser.agency.name}`}>{avatar}</div>;
  }

  return (
    <div className="flex items-center gap-2.5 rounded-md p-2">
      {avatar}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-neutral-900 dark:text-white">
          {agencyUser.name}
        </div>
        <div className="truncate text-xs font-medium text-neutral-400 dark:text-neutral-500">
          {agencyUser.agency.name}
        </div>
      </div>
    </div>
  );
};

type SidebarContentProps = {
  onOpenDocumentation: () => void;
  /** Closes the mobile drawer once a link inside it has been followed. */
  onNavigate?: () => void;
};

/**
 * Wireframe 2a: one column, page sub-tabs unfolding beneath the active page.
 * Also the body of the mobile drawer, which has no collapsed form.
 */
export const SidebarNavigation = ({
  onOpenDocumentation,
  onNavigate,
  onCollapse
}: SidebarContentProps & { onCollapse?: () => void }) => {
  const access = useNavigationAccess();
  const groups = filterNavigationGroups(navigationGroups, access);
  const settingsTabs = filterNavigationTabs(settingsPage.tabs ?? [], access);

  return (
    // grow, because the mobile drawer lays this out as a row flex item and it
    // would otherwise shrink to the width of the longest label.
    <div className="flex h-full min-h-0 grow flex-col bg-white dark:bg-neutral-800">
      <div className="flex shrink-0 items-center justify-between pl-5 pr-2.5 pb-2.5 pt-4">
        <CaraWordMark scale={0.4} />
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            title="Collapse sidebar"
            className="flex size-7 items-center justify-center rounded-md text-neutral-400 dark:text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white"
          >
            <span className="sr-only">Collapse sidebar</span>
            <PanelIcon className="size-4" />
          </button>
        )}
      </div>

      <div className="shrink-0 px-2.5 pb-2">
        <SearchRow />
      </div>

      <nav aria-label="Main" className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-1.5">
        {groups.map((group) => (
          <div key={group.name ?? group.pages[0].href}>
            {group.name && (
              // Labels the list rather than sitting inside it, so the name
              // reaches a screen reader as the list's name instead of as a
              // stray item between the rows.
              <div
                id={`nav-group-${group.name}`}
                className="px-2.5 pb-2 pt-4 text-[10.5px] uppercase font-semibold leading-none tracking-normal text-neutral-400 dark:text-neutral-500"
              >
                {group.name}
              </div>
            )}
            <ul role="list" aria-labelledby={group.name && `nav-group-${group.name}`}>
              {group.pages.map((page) => (
                <PageRow
                  key={page.href}
                  page={page}
                  tabs={filterNavigationTabs(page.tabs ?? [], access)}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <nav
        aria-label="Support and settings"
        className="max-h-96 shrink-0 overflow-y-auto border-t border-neutral-100 dark:border-neutral-700 px-2.5 py-2"
      >
        <ul role="list">
          <li className="mb-0.5">
            <button
              type="button"
              onClick={onOpenDocumentation}
              className={clsx(pageRowClassName(false), 'w-full')}
            >
              <PageRowBody
                page={{ name: 'Documentation', icon: BookOpenIcon, activeIcon: BookOpenIconSolid }}
              />
            </button>
          </li>
          <li className="mb-0.5">
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(pageRowClassName(false), 'w-full')}
            >
              <PageRowBody
                page={{ name: 'Community', icon: UserGroupIcon, activeIcon: UserGroupIconSolid }}
              />
            </a>
          </li>
          <PageRow page={settingsPage} tabs={settingsTabs} onNavigate={onNavigate} />
        </ul>
      </nav>

      <div className="shrink-0 px-2.5 pb-2.5 pt-1">
        <UserSummary />
      </div>
    </div>
  );
};

/**
 * Where clicking a rail icon goes. The flyout, when there is one, is extra
 * destinations — the icon itself always takes you to this one.
 */
type RailTarget =
  | { kind: 'href'; href: string }
  | { kind: 'external'; url: string }
  | { kind: 'action'; onClick: () => void };

/** One rail icon, and the flyout it opens when the section has more than one destination. */
type RailSection = {
  key: string;
  title: string;
  icon: NavigationPage['icon'];
  activeIcon: NavigationPage['activeIcon'];
  /** Hrefs that mark this section as the one you are currently inside. */
  hrefs: string[];
  target: RailTarget;
  /**
   * Pages listed in the hover flyout. Empty when the icon is the only
   * destination (Home, Documentation, Community, a lone page with no tabs),
   * so hovering it does not open a menu that just repeats the click.
   */
  rows: RailRow[];
};

type RailRow = { page: NavigationPage; tabs: NavigationTab[] };

const railSectionsFor = (
  groups: NavigationGroup[],
  access: NavigationAccess,
  onOpenDocumentation: () => void
): RailSection[] => {
  // Filtered here rather than by the caller so no rail section can be built
  // from raw tabs and quietly float one the caller may not open.
  const pageRow = (page: NavigationPage): RailRow => ({
    page,
    tabs: filterNavigationTabs(page.tabs ?? [], access)
  });

  return [
    ...groups.map((group) => {
      const rows = group.pages.map(pageRow);
      // A lone page with no tabs is the icon's target; a flyout would only
      // repeat that one link. Several pages, or tabs under one, need the menu.
      const hasFlyout = rows.length > 1 || rows.some((row) => row.tabs.length > 0);

      return {
        // A group of one (Home) has no header, so it borrows its page's name.
        key: group.name ?? group.pages[0].href,
        title: group.name ?? group.pages[0].name,
        icon: group.pages[0].icon,
        activeIcon: group.pages[0].activeIcon,
        hrefs: group.pages.map((page) => page.href),
        target: { kind: 'href' as const, href: group.pages[0].href },
        rows: hasFlyout ? rows : []
      };
    }),
    {
      key: 'documentation',
      title: 'Documentation',
      icon: BookOpenIcon,
      activeIcon: BookOpenIconSolid,
      hrefs: [],
      target: { kind: 'action' as const, onClick: onOpenDocumentation },
      rows: []
    },
    {
      key: 'community',
      title: 'Community',
      icon: UserGroupIcon,
      activeIcon: UserGroupIconSolid,
      hrefs: [],
      target: { kind: 'external' as const, url: COMMUNITY_URL },
      rows: []
    },
    {
      key: settingsPage.href,
      title: settingsPage.name,
      icon: settingsPage.icon,
      activeIcon: settingsPage.activeIcon,
      hrefs: [settingsPage.href],
      target: { kind: 'href' as const, href: settingsPage.href },
      rows: [pageRow(settingsPage)]
    }
  ];
};

const flyoutRowClassName = (active: boolean, tab: boolean) =>
  clsx(
    'flex items-center rounded-lg px-2.5 text-left transition-colors duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
    tab ? 'ml-2.5 h-7 text-xs mt-1' : 'h-9 text-sm',
    active
      ? clsx(
          'font-semibold',
          tab
            ? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-white'
            : 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400'
        )
      : clsx(
          'font-medium hover:bg-neutral-50 dark:hover:bg-white/5',
          tab ? 'text-neutral-500 dark:text-neutral-400' : 'text-neutral-700 dark:text-neutral-300'
        )
  );

const RailFlyout = ({
  section,
  top,
  onDismiss
}: {
  section: RailSection;
  top: number;
  onDismiss: () => void;
}) => {
  const isHrefActive = useIsHrefActive();

  return (
    <nav
      aria-label={section.title}
      className="absolute left-full z-30 w-56 animate-sidebar-flyout overflow-y-auto rounded-xl border border-neutral-100 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 shadow-xl motion-reduce:animate-none"
      style={{ top, maxHeight: `calc(100vh - ${top}px - 12px)` }}
    >
      <div className="px-2.5 pb-2 pt-1 text-[10.5px] font-semibold leading-none tracking-normal uppercase text-neutral-400 dark:text-neutral-500">
        {section.title}
      </div>
      {section.rows.map((row) => (
        <React.Fragment key={row.page.href}>
          <NavLink
            to={row.page.href}
            onClick={onDismiss}
            className={flyoutRowClassName(isHrefActive(row.page.href), false)}
          >
            <span className="truncate">{row.page.name}</span>
          </NavLink>
          {row.tabs.map((tab) => (
            <NavLink
              key={tab.href}
              to={`${row.page.href}/${tab.href}`}
              onClick={onDismiss}
              className={flyoutRowClassName(isHrefActive(`${row.page.href}/${tab.href}`), true)}
            >
              <span className="truncate">{tab.name}</span>
            </NavLink>
          ))}
        </React.Fragment>
      ))}
    </nav>
  );
};

const railIconClassName = (active: boolean) =>
  clsx(
    'flex size-9 items-center justify-center rounded-lg',
    'transition-colors duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
    active
      ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400'
      : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white'
  );

const RailIcon = ({
  section,
  active,
  expanded,
  onOpenFlyout,
  onDismissFlyout
}: {
  section: RailSection;
  active: boolean;
  expanded: boolean;
  onOpenFlyout: (top: number) => void;
  onDismissFlyout: () => void;
}) => {
  const Icon = active ? section.activeIcon : section.icon;
  const hasFlyout = section.rows.length > 0;
  // Hovering a direct-link icon must close a neighbour's flyout; otherwise the
  // previous section's menu would stay up until the pointer left the rail.
  const flyoutProps = hasFlyout
    ? {
        'aria-expanded': expanded,
        onMouseEnter: (event: React.MouseEvent<HTMLElement>) =>
          onOpenFlyout(event.currentTarget.offsetTop),
        onFocus: (event: React.FocusEvent<HTMLElement>) =>
          onOpenFlyout(event.currentTarget.offsetTop)
      }
    : { onMouseEnter: onDismissFlyout };

  const body = (
    <>
      <span className="sr-only">{section.title}</span>
      <Icon aria-hidden="true" className="size-5" />
    </>
  );

  const className = railIconClassName(active);

  if (section.target.kind === 'href') {
    return (
      <Link to={section.target.href} title={section.title} className={className} {...flyoutProps}>
        {body}
      </Link>
    );
  }

  if (section.target.kind === 'external') {
    return (
      <a
        href={section.target.url}
        target="_blank"
        rel="noopener noreferrer"
        title={section.title}
        className={className}
        {...flyoutProps}
      >
        {body}
      </a>
    );
  }

  return (
    <button
      type="button"
      title={section.title}
      className={className}
      onClick={section.target.onClick}
      {...flyoutProps}
    >
      {body}
    </button>
  );
};

/**
 * Wireframe 2b: the 64px rail. Each icon is the section's landing page
 * (Documentation opens the docs; Community is an external link). Hovering a
 * section that has more than one destination floats its pages and tabs.
 */
export const SidebarRail = ({
  onOpenDocumentation,
  onExpand
}: {
  onOpenDocumentation: () => void;
  onExpand?: () => void;
}) => {
  const access = useNavigationAccess();
  const isHrefActive = useIsHrefActive();
  const groups = filterNavigationGroups(navigationGroups, access);
  const sections = railSectionsFor(groups, access, onOpenDocumentation);

  // `top` is captured from the icon rather than derived from the index so the
  // flyout stays aligned however many sections the caller's flags leave.
  const [open, setOpen] = React.useState<{ key: string; top: number } | null>(null);
  const dismiss = React.useCallback(() => setOpen(null), []);

  // On the document, not the rail: a flyout opened by hover leaves focus wherever
  // it was, so a keydown handler on the rail would never see the key.
  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && dismiss();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, dismiss]);

  const openSection = sections.find((section) => section.key === open?.key);

  return (
    <div className="relative flex h-full flex-col items-center pb-2.5 pt-4" onMouseLeave={dismiss}>
      <CaraMark scale={0.44} />

      {onExpand && (
        <button
          type="button"
          onClick={onExpand}
          title="Expand sidebar"
          className="mt-3.5 flex h-9 items-center justify-center rounded-md text-neutral-400 dark:text-neutral-500 transition-colors duration-150 hover:bg-neutral-50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white"
        >
          <span className="sr-only">Expand sidebar</span>
          <PanelIcon className="size-4" />
        </button>
      )}

      {/* onMouseEnter dismisses for the same reason a direct-link RailIcon does:
          crossing this icon must close whichever flyout is open, not leave the
          previous section's menu standing over it. */}
      <div className={clsx(onExpand ? 'mt-1.5' : 'mt-3.5')} onMouseEnter={dismiss}>
        <SearchRailIcon />
      </div>

      <nav aria-label="Main" className="mt-1.5 flex flex-col items-center gap-1">
        {sections.map((section) => (
          <RailIcon
            key={section.key}
            section={section}
            active={section.hrefs.some(isHrefActive)}
            expanded={open?.key === section.key}
            onOpenFlyout={(top) => setOpen({ key: section.key, top })}
            onDismissFlyout={dismiss}
          />
        ))}
      </nav>

      <div className="flex-1" />
      <UserSummary compact />

      {openSection && open && openSection.rows.length > 0 && (
        <RailFlyout section={openSection} top={open.top} onDismiss={dismiss} />
      )}
    </div>
  );
};

/**
 * The fixed desktop sidebar. Renders its own container rather than taking one
 * from the dashboard because the rail's flyout must not be clipped, so the
 * expanded and collapsed forms need different overflow.
 */
export const Sidebar = ({
  collapsed,
  onSetCollapsed,
  onOpenDocumentation
}: {
  collapsed: boolean;
  /** Absent on routes that force the rail, which offer no toggle. */
  onSetCollapsed?: (collapsed: boolean) => void;
  onOpenDocumentation: () => void;
}) => (
  <div
    className={clsx(
      'hidden bg-white dark:bg-neutral-800 shadow transition-[width] duration-150 ease-in-out',
      'lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:flex-col',
      collapsed ? 'lg:w-16' : 'lg:w-64'
    )}
  >
    {collapsed ? (
      <SidebarRail
        onOpenDocumentation={onOpenDocumentation}
        onExpand={onSetCollapsed && (() => onSetCollapsed(false))}
      />
    ) : (
      <SidebarNavigation
        onOpenDocumentation={onOpenDocumentation}
        onCollapse={onSetCollapsed && (() => onSetCollapsed(true))}
      />
    )}
  </div>
);

/**
 * Stand-in for the sidebar while the root loader resolves. Matches the expanded
 * chrome so the shell does not shift once the real navigation arrives.
 */
export const SidebarSkeleton = () => (
  <div className="hidden border-r border-neutral-100 dark:border-neutral-700 bg-white dark:bg-neutral-800 lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:w-64 lg:flex-col">
    <div className="flex shrink-0 items-center px-3.5 pb-2.5 pt-4">
      <div className="h-[22px] w-24 animate-pulse rounded-md bg-neutral-200 dark:bg-neutral-700" />
    </div>
    <div className="flex-1 space-y-1 px-2.5 pt-2">
      {Array.from({ length: 10 }, (_, index) => (
        <div
          key={index}
          className="h-9 animate-pulse rounded-md bg-neutral-100 dark:bg-neutral-700"
        />
      ))}
    </div>
  </div>
);
